import { useState } from "react";
import { Sparkles, Send, Trash2, ChevronDown, ChevronUp, ExternalLink, Eye, Download, Link as LinkIcon, Copy, Beaker } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface LeadSubmission {
  id: string;
  name: string;
  email: string;
  phone: string;
  company: string;
  position: string;
  linkedin_url: string | null;
  score: number;
  created_at: string;
  enriched_data?: any;
  push_to_crm_at?: string | null;
  report_token?: string | null;
  report_password?: string | null;
  is_test_data?: boolean;
  source_project?: string | null;
}

interface MobileLeadCardProps {
  lead: LeadSubmission;
  onEnrich: () => void;
  onPushToCRM: () => void;
  onDelete: () => void;
  onViewEnriched: () => void;
  onViewQuiz: () => void;
  isEnriching: boolean;
  isPushing: boolean;
  formatPhoneNumber: (phone: string) => string;
  getBadgeColor: (score: number) => string;
  getBadgeLabel: (score: number) => string;
  onGeneratePDF?: () => void;
}

export function MobileLeadCard({
  lead,
  onEnrich,
  onPushToCRM,
  onDelete,
  onViewEnriched,
  onViewQuiz,
  isEnriching,
  isPushing,
  formatPhoneNumber,
  getBadgeColor,
  getBadgeLabel,
  onGeneratePDF,
}: MobileLeadCardProps) {
  const [expanded, setExpanded] = useState(false);
  const { toast } = useToast();

  return (
    <Card className="p-4 space-y-3">
      {/* PRIORITY 5: Test data badge */}
      <div className="flex flex-wrap gap-2">
        {lead.is_test_data && (
          <Badge variant="secondary">
            <Beaker className="h-3 w-3 mr-1" />
            TEST DATA
          </Badge>
        )}
        {lead.source_project && lead.source_project !== 'lifactor' && (
          <Badge 
            variant="outline" 
            className={
              lead.source_project === 'meta-ads'
                ? "bg-orange-100 dark:bg-orange-900 text-orange-700 dark:text-orange-300 border-orange-300 dark:border-orange-700"
                : "bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-700"
            }
          >
            {lead.source_project === 'meta-ads' ? '📣' : '📦'} {lead.source_project === 'pageleads' ? 'Pageleads' : lead.source_project === 'meta-ads' ? 'Meta Ads' : lead.source_project}
          </Badge>
        )}
      </div>
      
      {/* Priority 1: Always visible */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-base truncate">{lead.name}</h3>
          <p className="text-sm text-muted-foreground truncate">{lead.email}</p>
        </div>
        <Badge className={getBadgeColor(lead.score)}>
          {getBadgeLabel(lead.score)}
        </Badge>
      </div>

      <div className="flex items-center gap-2">
        <span className="text-2xl font-bold">{lead.score}</span>
        <span className="text-sm text-muted-foreground">punktów</span>
      </div>

      {/* Status badges */}
      <div className="flex flex-wrap gap-2">
        {lead.enriched_data && (
          <Badge variant="outline" className="text-xs">
            ✨ Wzbogacony
          </Badge>
        )}
        {lead.push_to_crm_at && (
          <Badge variant="outline" className="text-xs">
            ✅ Wysłano do CRM
          </Badge>
        )}
      </div>

      {/* Expandable section */}
      {expanded && (
        <div className="space-y-2 pt-2 border-t">
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div>
              <span className="text-muted-foreground">Telefon:</span>
              <p className="font-medium">{formatPhoneNumber(lead.phone)}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Firma:</span>
              <p className="font-medium truncate">{lead.company}</p>
            </div>
            <div className="col-span-2">
              <span className="text-muted-foreground">Stanowisko:</span>
              <p className="font-medium">{lead.position}</p>
            </div>
            {lead.linkedin_url && (
              <div className="col-span-2">
                <a
                  href={lead.linkedin_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary text-sm flex items-center gap-1 hover:underline"
                >
                  <ExternalLink className="h-3 w-3" />
                  LinkedIn
                </a>
              </div>
            )}
            <div className="col-span-2 text-xs text-muted-foreground">
              Utworzono: {new Date(lead.created_at).toLocaleDateString("pl-PL")}
            </div>
            {lead.report_token && lead.report_password && (
              <div className="col-span-2 pt-2 border-t space-y-2">
                <div className="text-xs font-semibold text-muted-foreground">
                  Link do raportu:
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      const url = `${window.location.origin}/raport-zabezpieczony/${lead.report_token}`;
                      navigator.clipboard.writeText(url);
                      toast({
                        title: "Skopiowano!",
                        description: "Link do raportu został skopiowany"
                      });
                    }}
                    className="flex-1"
                  >
                    <LinkIcon className="h-3 w-3 mr-1" />
                    Kopiuj link
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      window.open(`/raport-zabezpieczony/${lead.report_token}`, '_blank');
                    }}
                  >
                    <ExternalLink className="h-3 w-3" />
                  </Button>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">Hasło:</span>
                  <code className="text-xs bg-muted px-2 py-1 rounded font-mono flex-1">
                    {lead.report_password}
                  </code>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      navigator.clipboard.writeText(lead.report_password || '');
                      toast({
                        title: "Skopiowano!",
                        description: "Hasło zostało skopiowane"
                      });
                    }}
                  >
                    <Copy className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-col gap-2 pt-2">
        {/* Primary action buttons - always visible for quiz and enrichment */}
        <div className="grid grid-cols-1 gap-2">
          {lead.score > 0 && (
            <Button
              variant="default"
              size="sm"
              onClick={onViewQuiz}
            >
              <Eye className="h-4 w-4 mr-2" />
              Zobacz odpowiedzi quizu
            </Button>
          )}
          
          {lead.enriched_data && (
            <Button
              variant="default"
              size="sm"
              onClick={onViewEnriched}
            >
              <Eye className="h-4 w-4 mr-2" />
              Zobacz dane wzbogacenia
            </Button>
          )}
        </div>

        {/* Secondary actions */}
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setExpanded(!expanded)}
            className="flex-1"
          >
            {expanded ? (
              <>
                <ChevronUp className="h-4 w-4 mr-1" />
                Zwiń
              </>
            ) : (
              <>
                <ChevronDown className="h-4 w-4 mr-1" />
                Rozwiń
              </>
            )}
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="flex-1">
                Akcje
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48 bg-background z-50">
              {onGeneratePDF && (
                <DropdownMenuItem onClick={onGeneratePDF}>
                  <Download className="h-4 w-4 mr-2" />
                  Pobierz PDF
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={onEnrich} disabled={isEnriching}>
                <Sparkles className="h-4 w-4 mr-2" />
                {isEnriching ? "Wzbogacanie..." : "Wzbogać AI"}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onPushToCRM} disabled={isPushing || !!lead.push_to_crm_at}>
                <Send className="h-4 w-4 mr-2" />
                {isPushing ? "Wysyłanie..." : lead.push_to_crm_at ? "Wysłano" : "Salesflare"}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onDelete} className="text-destructive">
                <Trash2 className="h-4 w-4 mr-2" />
                Usuń
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </Card>
  );
}
